import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { searchUsers, type UserSummary } from '../../../api/users';
import { addRoomMembers } from '../../../api/chat';
import type { RoomMemberDto } from '../../../types/chat';
import { Check, Search, X } from '../../../icons';
import { Avatar } from '../../../components/shared/Avatar';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';
import { filterInviteCandidates, inviteRoleBadgeKey } from '../../../lib/chat-invite-policy';

// 선검색(pre-load) + 검색 결과 표시 상한. backend listByInstitution 가 institution-scoped 전체에서
// 이 개수만큼 반환(.chat-modal-results 가 max-height 280px 스크롤이라 표시 안전).
const LIST_LIMIT = 30;

// chat-invite-allow-admins (2026-08-11) — 초대 후보 role 정책은 lib/chat-invite-policy 로 이관(SSOT).
// 이전: SYSTEM_ADMIN/ADMIN/AI_BOT 제외(MANAGER/STAFF 만) → 현재: 사람 계정 전부 허용, AI_BOT 만 제외.
// 서버(POST /chat/rooms/:id/members)·모바일은 원래부터 role 무관이라 웹만 막혀 있던 비대칭을 해소.

interface InviteMembersModalProps {
  roomId: string;
  /** 이미 방에 있는 멤버 userId — 결과에서 '이미 참여 중'으로 비활성. */
  existingMemberIds: string[];
  onClose: () => void;
  /** 초대 성공 → 갱신된 전체 멤버 목록 (ChatArea 가 members 상태 반영). */
  onInvited: (members: RoomMemberDto[]) => void;
}

/**
 * InviteMembersModal (chat-room-invite — 2026-06-30) — 우측 참가자 바의 '+' 가 여는 초대 모달.
 *
 * NewChatModal 의 people 검색·다중선택 UX 와 동일 CSS(chat-modal-*) 재사용. addRoomMembers 호출 →
 * 성공 시 갱신된 멤버 목록을 onInvited 로 전달. 이미 참여 중인 사용자는 결과에서 비활성 표시.
 */
export function InviteMembersModal({ roomId, existingMemberIds, onClose, onInvited }: InviteMembersModalProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSummary[]>([]);
  const [selected, setSelected] = useState<UserSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const existing = new Set(existingMemberIds);

  // 사용자 검색 — debounce 250ms + AbortController (NewChatModal 동형).
  // 모달 열림(빈 쿼리) 시점에도 기관 회원을 **선검색(pre-load)** 해 프로필 아이콘+이름 목록을 즉시
  // 노출(요청 2026-06-30). 빈 검색(q='')은 backend listByInstitution 가 OR 절 없이 institution-scoped
  // 전체를 반환 → 백엔드 변경 0. 빈 쿼리는 debounce 없이 즉시(0ms), 입력 시에만 250ms debounce.
  useEffect(() => {
    const q = query.trim();
    const ctrl = new AbortController();
    setSearching(true);
    const id = window.setTimeout(() => {
      searchUsers(q, LIST_LIMIT, ctrl.signal)
        .then((users) => setResults(filterInviteCandidates(users)))
        .catch(() => { /* abort/검색 실패 — 무시 */ })
        .finally(() => setSearching(false));
    }, q.length === 0 ? 0 : 250);
    return () => {
      window.clearTimeout(id);
      ctrl.abort();
    };
  }, [query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (u: UserSummary) => {
    setSelected((prev) =>
      prev.some((s) => s.id === u.id) ? prev.filter((s) => s.id !== u.id) : [...prev, u],
    );
  };

  const canInvite = selected.length >= 1 && !inviting;

  const handleInvite = useCallback(async () => {
    if (selected.length === 0 || inviting) return;
    setInviting(true);
    setError(null);
    try {
      const { members } = await addRoomMembers(roomId, selected.map((s) => s.id));
      onInvited(members);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : tChat('chat.invite.failed'));
      setInviting(false);
    }
  }, [inviting, onClose, onInvited, roomId, selected]);

  // chat-modal-portal (2026-07-02) — body 로 portal(채팅 트리 z-index:1 트랩 탈출 → 항상 최상단).
  return createPortal(
    <div className="chat-modal-overlay" onMouseDown={onClose} role="presentation">
      <div
        className="chat-modal"
        role="dialog"
        aria-modal="true"
        aria-label={tChat('chat.invite.title')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="chat-modal-header">
          <h2 className="chat-modal-title">{tChat('chat.invite.title')}</h2>
          <button type="button" className="chat-modal-close" onClick={onClose} aria-label={tChat('chat.invite.cancel')}>
            <X size={18} />
          </button>
        </header>

        <div className="chat-modal-body">
          {selected.length > 0 && (
            <ul className="chat-modal-chips" aria-label={tChat('chat.invite.selected_aria')}>
              {selected.map((u) => (
                <li key={u.id} className="chat-modal-chip">
                  <span>{u.name}</span>
                  <button type="button" onClick={() => toggle(u)} aria-label={tChat('chat.invite.remove_aria', { name: u.name })}>
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="chat-modal-search">
            <Search size={16} className="chat-modal-search-icon" />
            <input
              type="search"
              className="chat-modal-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tChat('chat.invite.search_placeholder')}
              aria-label={tChat('chat.invite.search_placeholder')}
              autoFocus
            />
          </div>

          <ul className="chat-modal-results" role="listbox" aria-label={tChat('chat.invite.results_aria')}>
            {searching && <li className="chat-modal-hint">{tChat('chat.invite.searching')}</li>}
            {!searching && results.length === 0 && (
              <li className="chat-modal-hint">{tChat('chat.invite.no_results')}</li>
            )}
            {results.map((u) => {
              const already = existing.has(u.id);
              const on = selected.some((s) => s.id === u.id);
              // 관리자 계정은 이름 옆 배지로 구분 — 사람 후보와 섞여도 오초대하지 않도록.
              const badgeKey = inviteRoleBadgeKey(u.role);
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={on}
                    aria-disabled={already}
                    disabled={already}
                    className={`chat-modal-result${on ? ' chat-modal-result--on' : ''}`}
                    onClick={() => !already && toggle(u)}
                  >
                    <Avatar userId={Number(u.id)} name={u.name ?? '?'} src={u.avatarUrl} size="sm" />
                    <span className="chat-modal-result-meta">
                      <span className="chat-modal-result-name">
                        {u.name}
                        {badgeKey && <span className="chat-modal-result-badge">{tChat(badgeKey)}</span>}
                      </span>
                      <span className="chat-modal-result-sub">
                        {already ? tChat('chat.invite.already_member') : (u.position ?? u.email)}
                      </span>
                    </span>
                    {on && <Check size={16} className="chat-modal-result-check" />}
                  </button>
                </li>
              );
            })}
          </ul>

          {error && <p className="chat-modal-error" role="alert">{error}</p>}
        </div>

        <footer className="chat-modal-footer">
          <button type="button" className="chat-modal-cancel" onClick={onClose}>{tChat('chat.invite.cancel')}</button>
          <button
            type="button"
            className="chat-modal-submit"
            onClick={() => void handleInvite()}
            disabled={!canInvite}
          >
            {inviting ? tChat('chat.invite.submitting') : tChat('chat.invite.submit')}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
